<template>
  <Loader
    :value="promises.fetch"
    :message="$t('loading', { resource: $tc('unitCatalog', 1).toLowerCase() }) + '...'"
    class="residential-templates"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title">
        <template #actions>
          <Actions placement="residentialTemplates" />

          <DropdownComponent v-if="canEdit" :closeOnClick="false">
            <ButtonComponent :label="$t('add')" :icon="['fal', 'plus']" theme="primary" />
            <template #dropdown="{ close, visible }">
              <ButtonComponent
                tag="router-link"
                :to="{ name: 'residential-template-create', query: { returnTo: $route.path } }"
                :label="$tc('unitCatalog', 1)"
                :icon="['fal', 'plus']"
                @click="close"
              />
              <ButtonComponent
                tag="router-link"
                :label="$tc('import')"
                :to="{ name: 'import', params: { type: 'unit-catalog' } }"
                :icon="['fal', 'file-import']"
                @click="close"
              />
            </template>
          </DropdownComponent>
        </template>
      </HeaderComponent>
      <FilterComponent
        v-model="filter"
        :items="filterItems"
        :default="defaultFilter"
        :options="{
          search: { placeholder: [$t('name')] },
          includeArchived: { label: $t('showArchived') },
        }"
        class="mb-2"
      />
      <div class="residential-templates__content">
        <ResidentialTemplateCardItem v-for="item in itemsCategorised" :value="item" />
      </div>
      <EmptyState
        v-if="!visibleItems.length && !states.fetch"
        :title="$t('noX', { x: this.$tc('unit', 2).toLowerCase() })"
        :icon="['fal', 'puzzle-piece']"
        action-portal
      />
    </div>
  </Loader>
</template>

<script>
import FilterMixin from '@/components/Filter'
import HeaderComponent from '@/components/Header'
import Actions from '@/components/Actions'
import ResidentialTemplateCardItem from '@/components/ResidentialTemplate/CardItem'
import Card from '@/components/Card'
import EmptyState from '@/components/EmptyState'
import { mapActions, mapState } from 'vuex'
import { DropdownComponent } from 'vue-elder-dropdown'
import { Debounce } from '@/utils'

export default {
  mixins: [FilterMixin({ default: { search: '', includeArchived: false } })],
  watch: {
    filter: {
      handler: 'fetch',
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapState('ResidentialTemplate', ['items', 'promises', 'states']),
    canEdit() {
      return this.$store.getters.hasAccess('residentialTemplates')
    },
    filterItems() {
      return ['search', this.canEdit ? 'checkbox:includeArchived' : null].filter(Boolean)
    },
    visibleItems() {
      if (this.canEdit) return this.items

      return this.items.filter(item => item.roles.salesman.some(({ id }) => id === this.$store.state.user.id))
    },
    itemsCategorised() {
      let items = this.visibleItems.reduce((acc, curr) => {
        acc[curr.id] = {
          parent: curr,
          items: this.$path('customFields.metadata.subtemplate.items', curr)
            ? curr.customFields.metadata.subtemplate.items.map(i => this.getTemplate(i)).filter(Boolean)
            : [],
        }

        return acc
      }, {})

      const parentIds = Object.entries(items).map(([key]) => key)

      parentIds.forEach(p => {
        const match = Object.entries(items).find(([key, value]) => {
          return value.items.find(t => t.id === p)
        })

        if (match) {
          //delete items that is subtemplate.items
          delete items[p]
        }
      })

      return Object.fromEntries(
        Object.entries(items).sort(([, a], [, b]) => {
          const aHasItems = a.items && a.items.length > 0
          const bHasItems = b.items && b.items.length > 0

          return Number(bHasItems) - Number(aHasItems)
        }),
      )
    },
  },
  methods: {
    ...mapActions('ResidentialTemplate', {
      fetch: Debounce(function (dispatch) {
        return dispatch('fetch', { filter: this.filter })
      }, 200),
    }),
    getTemplate(id) {
      return this.visibleItems.find(t => t.id === id)
    },
  },
  metaInfo() {
    return {
      title: this.$tc('unitCatalog', 1),
    }
  },
  components: {
    ResidentialTemplateCardItem,
    Card,
    HeaderComponent,
    Actions,
    EmptyState,
    DropdownComponent,
  },
}
</script>

<style lang="scss">
.residential-templates {
  background-color: $background-grey;

  &__content {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: $form-gap;
  }
}
</style>
